import React, { useState } from "react";
import { CartItem, useCart } from "../../../context/CartContext";
import { calculatePrice, plausibleEvent, toLocalNumber } from "../../../utilities/helper";
import { useGetChairInformation } from "../../../api/hooks/useChairs";
import { useChair } from "../../../context/ChairContext";
import { useGetCurrentUserId, useGetUserSettings } from "../../../api/hooks/useUserInfo";
import { useStep } from "../../../context/stepContext";
import { Step } from "./Step";
import { Loader } from "../../Loader";
import { ModelViewer } from "../modelViewer/ModelViewer";
import { usePlaceOrder } from "../../../api/hooks/useOrder";

type CartEntryProps = {
    item: CartItem;
};

const CartEntry = (props: CartEntryProps) => {
    const { changeCount, removeItem, setLoadFromCart } = useCart();
    const { setChair } = useChair();
    const { setStep } = useStep();
    const chair = props.item.chair;
    const chairInformationQuery = useGetChairInformation({ id: chair.id });
    const measurements =
        chairInformationQuery.isSuccess && chairInformationQuery.data.chairEntries[0]
            ? chairInformationQuery.data.chairEntries[0].measurements
            : "";

    return (
        <div className={"cart-item"}>
            <div className={"item-image item-info"}>
                <ModelViewer chairModel={chair} animate={false} />
            </div>
            <div className={"item-details item-info"}>
                <h4>
                    {chair.modelName}-{chair.variant}
                </h4>
                {chairInformationQuery.isFetching && !chairInformationQuery.isRefetching ? (
                    <Loader />
                ) : (
                    <ul className={"configuration"}>
                        <li className={"config-entry"}>{measurements}</li>
                        <li className={"config-entry"}>
                            Gestell: {chair.frame.name} | {chair.frame.texture.name}
                        </li>
                        <li className={"config-entry"}>
                            Bezug: {chair.cover.className} | {chair.cover.catalog.name} | {chair.cover.color.name}
                        </li>
                        <li className={"config-entry"}>Zubehör: {chair.extra.name}</li>
                    </ul>
                )}
            </div>
            <div className={"item-count item-info"}>
                <div className={"cart-item-small-heading"}>Anzahl</div>
                <select
                    className={"cart-select"}
                    defaultValue={props.item.count}
                    onChange={(e) => {
                        changeCount(props.item.id, Number(e.currentTarget.value));
                    }}
                >
                    {Array.from(Array(Math.max(50, props.item.count)).keys()).map((i) => {
                        const option = i + 1;
                        return (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        );
                    })}
                </select>
            </div>
            <div className={"item-cost-single item-info"}>
                <div className={"cart-item-small-heading"}>€/Stk</div>
                <div className={"cart-item-cost"}>€ {toLocalNumber(calculatePrice(chair))}</div>
            </div>
            <div className={"item-cost-all item-info"}>
                <div className={"cart-item-small-heading"}>Gesamtpreis</div>
                <div className={"cart-item-cost-sum"}>€ {toLocalNumber(calculatePrice(chair) * props.item.count)}</div>
            </div>
            <div className={"item-buttons"}>
                <button
                    className={"button icon-button edit"}
                    onClick={() => {
                        setLoadFromCart(true);
                        setChair(chair);
                        setStep(Step.OVERVIEW);
                    }}
                    title={"Konfiguration als Basis für neue Konfiguration verwenden"}
                >
                    Bearbeiten
                </button>
                <button
                    className={"button icon-button remove"}
                    onClick={() => {
                        removeItem(props.item.id);
                    }}
                >
                    Löschen
                </button>
            </div>
        </div>
    );
};

const CartNote = () => {
    const { note, setNote } = useCart();
    const [showInput, setShowInput] = useState<boolean>(!!note);

    return (
        <div className={"cart-note"}>
            <button
                className={"button primary icon-button add"}
                onClick={(e) => {
                    e.currentTarget.blur();
                    if (showInput) {
                        setNote("");
                    }
                    setShowInput(!showInput);
                }}
            >
                {!showInput ? "Anmerkung hinzufügen" : "Anmerkung entfernen"}
            </button>
            {showInput ? (
                <textarea
                    className={"note-field"}
                    rows={4}
                    value={note}
                    onChange={(e) => {
                        setNote(e.currentTarget.value);
                    }}
                />
            ) : null}
        </div>
    );
};

export const CartStep = () => {
    const { cart } = useCart();
    const [error, setError] = useState<string>("");
    const [ordering, setOrdering] = useState<boolean>(false);
    const { setStep } = useStep();

    const userIdQuery = useGetCurrentUserId();
    const userId = userIdQuery.isSuccess ? userIdQuery.data.users[0].id : null;

    const userSettingsQuery = useGetUserSettings(userId ?? "");
    const shippingCost = userSettingsQuery.isSuccess ? userSettingsQuery.data.users[0].shippingCost ?? 0 : 0;

    const placeOrderMutation = usePlaceOrder();

    const getCostSum = () => {
        let sum = 0;
        cart.forEach((item) => {
            sum += calculatePrice(item.chair) * item.count;
        });
        return sum;
    };

    return (
        <div className={"cart-step"}>
            <div className={"cart-content"}>
                <div className={"heading"}>
                    <h2>Ihr Warenkorb:</h2>
                    <h2>{cart.length} Artikel</h2>
                </div>
                <ul className={"cart-entries"}>
                    {cart.map((item) => {
                        return <CartEntry key={item.id} item={item} />;
                    })}
                </ul>
            </div>
            <div className={"cart-summary"}>
                {userId != null && userSettingsQuery.isLoading && !userSettingsQuery.isRefetching ? (
                    <Loader />
                ) : (
                    <div className={"content"}>
                        <h2 className={"white"}>Zusammenfassung</h2>
                        <ul className={"summary-fields"}>
                            <li className={"summary-field"}>
                                <span>Gesamt</span>
                                <span>€ {toLocalNumber(getCostSum())}</span>
                            </li>
                            {userSettingsQuery.isSuccess ? (
                                <li className={"summary-field"}>
                                    <span>Versandkosten</span>
                                    <span>€ {toLocalNumber(shippingCost)}</span>
                                </li>
                            ) : null}
                            <li className={"divider"}></li>
                            <li className={"summary-field sum"}>
                                <span>Summe</span>
                                <span className={"total"}>€ {toLocalNumber(getCostSum() + shippingCost)}</span>
                            </li>
                        </ul>
                        {userId ? <CartNote /> : null}
                        {error !== "" ? <div className={"error"}>{error}</div> : null}
                        {userId ? (
                            <button
                                className={`button primary icon-button order ${ordering ? "ordering" : ""}`}
                                onClick={async (e) => {
                                    setOrdering(true);
                                    e.currentTarget.blur();
                                    plausibleEvent("order-goods", `${cart.length}`);
                                    const response = await placeOrderMutation.mutateAsync();
                                    if (response.status === 200) {
                                        setStep(Step.THANK_YOU);
                                    } else {
                                        const body = await response.json();
                                        setError(body.message);
                                    }
                                    setOrdering(false);
                                }}
                            >
                                Jetzt Bestellen
                            </button>
                        ) : (
                            <a href="/login?from=cart" className={"button primary order"}>
                                Zum Bestellen einloggen
                            </a>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
